import {document} from "@acng/frontend-bounty";
import {queryAll} from "@acng/frontend-bounty/dom/query.js";
import {createMap, createWeakMap, get, set, store} from "@acng/frontend-bounty/collection.js";
import {create, store as storeObject} from "@acng/frontend-bounty/object.js";

import {
  CTX_ALERT,
  CTX_OBSERVE,
  CTX_PROVIDE,
  CTX_REVOKE,
  CTX_SUBSCRIBE_TAGNAME,
  CTX_UNOBSERVE,
  CTX_VALUE,
} from "../minify.js";

const MODULE = "relativity/context-provider";

/**
 * TODO Fix type
 *
 * @type {WeakMap<Element, {
 *   [key: symbol]: {
 *     [key: symbol]: (value: any, previous?: any) => any;
 *   }
 * }>}
 */
const consumers = createWeakMap();

/**
 * Context may have a selector.
 *
 * @type {Map<symbol, ?string>}
 */
const subscriptions = createMap();

/**
 * Assign selector to a Context.
 *
 * @internal
 * @param {symbol} contextSymbol
 * @param {string} selector
 */
export const subscribe = (contextSymbol, selector) => {
  const selectors = get(subscriptions, contextSymbol);

  if (!selectors) {
    set(subscriptions, contextSymbol, selector);
    //function escapeRegex(string) {
    //  return string.replace(/[/\-\\^$*+?.()|[\]{}]/g, '\\$&');
    //}
  } else if (!selectors.match(new RegExp(`[,^]${CSS.escape(selector)}[,$]`))) {
    set(subscriptions, contextSymbol, `${selectors},${selector}`);
  }
};

/**
 * @template T
 * @param {T extends undefined ? never : T} value
 * @param {symbol} contextSymbol
 * @returns {import('./context-interface').Context<T>}
 */
export const createContextProvider = (value, contextSymbol) => {
  const providerSymbol = Symbol();
  /**
   * @param {?Element} provider
   * @param {T extends undefined ? never : T} nextValue
   * @param {T extends undefined ? never : T} previousValue
   */
  const provide = (provider, nextValue, previousValue) => {
    value = nextValue;
    const selectors = get(subscriptions, contextSymbol);

    if (selectors) {
      for (const element of queryAll(provider ?? document, selectors)) {
        if (!element.isConnected) continue;
        const handlers = get(consumers, element)?.[providerSymbol];
        if (handlers) {
          for (const consumerSymbol of Object.getOwnPropertySymbols(handlers)) {
            const callback = handlers[consumerSymbol];
            DEBUG: if (element.hasAttribute('debug')) {
              console.info(MODULE, 'push', {provider, element, contextSymbol, callback});
            }
            callback(value, previousValue);
          }
        }
      }
    }
    return nextValue;
  };

  /**
   * @param {Element} element
   * @param {symbol | undefined} consumerSymbol
   * @param {(value: T, previous?: T) => unknown} fn
   */
  const putConsumer = (element, consumerSymbol, fn) => {
    consumerSymbol ??= providerSymbol;
    storeObject(
      store(consumers, element, () => create()),
      providerSymbol,
      () => create()
    )[consumerSymbol] = fn;
  };

  /**
   * @param {Element} element
   * @param {symbol | undefined} consumerSymbol
   */
  const deleteConsumer = (element, consumerSymbol) => {
    const callbacks = get(consumers, element)?.[providerSymbol];
    if (callbacks) {
      delete callbacks[consumerSymbol ?? providerSymbol];
    }
  };

  return {
    [CTX_PROVIDE]: (providerElement, nextValue) =>
      provide(providerElement, nextValue, value),
    [CTX_REVOKE]: () => {},
    [CTX_VALUE]: () => value,
    [CTX_SUBSCRIBE_TAGNAME]: (selector) => subscribe(contextSymbol, selector),
    [CTX_OBSERVE]: (element, handler, consumerSymbol) => {
      DEBUG: if (element.hasAttribute('debug')) console.debug(MODULE, 'watch', {element, value, handler});
      putConsumer(element, consumerSymbol, handler);
      return handler(value);
    },
    [CTX_ALERT]: (element, handler, consumerSymbol) => putConsumer(element, consumerSymbol, (value) => {
      DEBUG: if (element.hasAttribute('debug')) console.debug(MODULE, 'alert', {element, handler});
      deleteConsumer(element, consumerSymbol);
      handler(value);
    }),
    [CTX_UNOBSERVE]: deleteConsumer,
  };
};

/**
 * Create an `Observable` to provide and observe values on DOM Elements.
 *
 * @template T
 * @param {T extends undefined ? never : T} value
 * The initial value must be defined.
 */
export function createGlobalContext(value) {
  return createContextProvider(value, Symbol());
}
